import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import type { FindAssetCustomFieldValueResponseDto } from '@evimo/types/api/asset-custom-field-value/find-asset-custom-field-value/find-asset-custom-field-value.response.dto';
import { AssetCustomFieldType } from '@evimo/types/enums/asset-custom-field-type';
import dayjs, { type Dayjs } from 'dayjs';

export type CustomFieldFormValue = string | number | boolean | Dayjs | null;

export type CustomFieldFormValues = Record<string, CustomFieldFormValue>;

export function toStorageValue(
  field: FindAssetCustomFieldResponseDto,
  value: CustomFieldFormValue,
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  switch (field.fieldType) {
    case AssetCustomFieldType.BOOLEAN:
      return value === true || value === 'true' ? 'true' : 'false';
    case AssetCustomFieldType.NUMBER:
      return String(value);
    case AssetCustomFieldType.DATE:
      if (dayjs.isDayjs(value)) {
        return value.toISOString();
      }
      return String(value);
    case AssetCustomFieldType.TEXT:
    default:
      return String(value);
  }
}

export function fromStorageValue(
  field: FindAssetCustomFieldResponseDto,
  value: string | null | undefined,
): CustomFieldFormValue {
  if (value == null || value === '') {
    return field.fieldType === AssetCustomFieldType.BOOLEAN ? false : null;
  }

  switch (field.fieldType) {
    case AssetCustomFieldType.BOOLEAN:
      return value === 'true';
    case AssetCustomFieldType.NUMBER: {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    case AssetCustomFieldType.DATE:
      return dayjs(value);
    case AssetCustomFieldType.TEXT:
    default:
      return value;
  }
}

export function buildInitialCustomFieldValues(
  fields: FindAssetCustomFieldResponseDto[],
  values: FindAssetCustomFieldValueResponseDto[],
): CustomFieldFormValues {
  const byFieldId = new Map(
    values.map((value) => [value.assetCustomFieldId, value]),
  );
  const result: CustomFieldFormValues = {};
  for (const field of fields) {
    result[field.id] = fromStorageValue(
      field,
      byFieldId.get(field.id)?.value ?? null,
    );
  }
  return result;
}

export function buildValueIdMap(
  values: FindAssetCustomFieldValueResponseDto[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const value of values) {
    if (value.assetCustomFieldId && value.id) {
      map[value.assetCustomFieldId] = value.id;
    }
  }
  return map;
}

export function getUnitFromRules(
  rules: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!rules || typeof rules !== 'object') {
    return undefined;
  }
  const maybeUnit = (rules as { unit?: unknown }).unit;
  return typeof maybeUnit === 'string' && maybeUnit.trim()
    ? maybeUnit.trim()
    : undefined;
}
